/**
 * SeatReservation Component
 *
 * This component handles the seat reservation cancellation process.
 * It displays flight information and allows users to select flights for seat cancellation.
 *
 * @component
 * @extends {Component}
 */

import React, { Component } from 'react';
import { Link } from 'react-router';
import _ from 'lodash';

import './seat-reservation.scss';

import tracking from '@/_helper/tracking';
import { convertDate } from '@/_helper/mapper';

class SeatReservation extends Component {
    constructor(props) {
        super(props);

        this.state = {
            bookingData: this.props.bookingData,
            legData: this.props.bookingData.data.serviceInformation.flight,
        };
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (prevProps.pageState === 'loading' && this.props.pageState === 'loaded') {
            this.enrichSeatReservationData();
        }
    }

    componentDidMount() {
        // Check if cancellation is allowed
        if (
            !this.props.bookingData?.data?.allowedInteractions?.cancellation?.partialCancellation['seatReservation'] ||
            window.deactivateServices.includes('partialCancellationSeatReservation')
        ) {
            window.location.href = '#/stornierungsoptionen';
            return false;
        }

        if (this.props.cancellationData !== null) {
            this.enrichSeatReservationData();
        }
    }

    /**
     * Enriches the seat reservation data with cancellation information
     */
    enrichSeatReservationData() {
        const { cancellationData } = this.props;
        const data = this.props.bookingData?.data;
        const { bookedSeats, flight } = data.serviceInformation;
        const { flightDetails } = data?.frontendParams;

        let titleMapping = {
            outbound: 'Hinflug',
            inbound: 'Rückflug',
        };

        let legData = cancellationData ? {} : flight;
        const connectionLine = (value, i) => {
            let departureDate = new Date(convertDate(value.depDate));
            let dateString;

            const options = {
                weekday: 'short',
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
            };

            dateString =
                departureDate.toLocaleString('de-DE', options) +
                ' ' +
                value.depTime +
                ' Uhr' +
                ' - ' +
                value.arrTime +
                ' Uhr';

            return `<div>${value.depAirportDescription} (${value.depAirportCode}) - ${value.arrAirportDescription} (${value.arrAirportCode}) mit ${value.airline} ${value.carrier} ${value.flightNumber}</div><div>${dateString}</div>`;
        };

        if (cancellationData) {
            Object.entries(cancellationData).forEach((cancellationDataValues) => {
                let offerKey = cancellationDataValues[0].split('-')[0];

                let bookedSeatsIndex = Object.values(bookedSeats)
                    .map((e) => e.offerKey)
                    .indexOf(offerKey);

                if (bookedSeats[bookedSeatsIndex]) {
                    _.forEach(flightDetails, function (value, key) {
                        if (
                            _.filter(value, {
                                depAirportCode: bookedSeats[bookedSeatsIndex].departureAirport,
                            }).length > 0
                        ) {
                            let getBookedSeatsByLeg = [];
                            Object.values(value).forEach((legDataItem, legDataIndex) => {
                                _.filter(bookedSeats, function (bookedSeatsEntry) {
                                    if (bookedSeatsEntry.departureAirport === legDataItem.depAirportCode) {
                                        let cancellationOfferKeyRef = Object.keys(cancellationData).filter(
                                            (cancellationDataEntry) =>
                                                cancellationDataEntry.includes(bookedSeatsEntry.offerKey)
                                        )[0];

                                        if (cancellationOfferKeyRef) {
                                            getBookedSeatsByLeg[bookedSeatsEntry.offerKey] = {};
                                            getBookedSeatsByLeg[bookedSeatsEntry.offerKey].cancellationAllowed =
                                                cancellationData[cancellationOfferKeyRef].cancellationAllowed;
                                        }
                                    }
                                });

                                legData[key] = [];
                                legData[key][legDataIndex] = {
                                    title: `${titleMapping[key]}${Object.values(value).length > 1 ? `: Teilstrecke ${legDataIndex + 1}` : ``}`,
                                    connection: connectionLine(legDataItem),
                                    entries: getBookedSeatsByLeg,
                                };
                            });
                        }
                    });
                }
            });
        }

        // store data
        this.setState({
            legData: legData,
        });
    }

    /**
     * Renders the flight cards for seat cancellation
     * @returns {JSX.Element} The rendered cards
     */
    Cards() {
        let { legData } = this.state;

        let $this = this;

        const entries = [];

        const logic = (item, index) => {
            if (typeof item === 'string') {
                return false;
            }

            const { title, connection } = item;

            const ref = [];
            Object.entries(item.entries).forEach((value, key) => {
                if (value[1].cancellationAllowed) {
                    ref.push(value[0]);
                }
            });

            let getCancelledSeats = Object.entries(item.entries).filter((o) => !o[1].cancellationAllowed);

            let isActive = getCancelledSeats.length === 0;

            let cardState = isActive ? 'active' : 'cancelled';

            let cancellationbuttonText = isActive
                ? `${Object.keys(ref).length === 1 ? 'Platz' : 'Plätze'} für diesen Flug stornieren`
                : `${Object.keys(ref).length === 1 ? 'Platz für diesen Flug ist' : 'Plätze für diesen Flug sind'} storniert`;

            let moreCancellationAllowed = getCancelledSeats.length > 0 && Object.keys(ref).length > 0;
            let moreCancellationAllowedButtonText = `${Object.keys(ref).length === 1 ? 'Weiterer Platz stornieren' : 'Weitere Plätze stornieren'}`;

            if (item.connection) {
                entries.push(
                    <section className={`card ${cardState}`} key={`${item.title}`}>
                        <div className="details">
                            <img
                                className="carrier-logo"
                                src="//www.aurumtours.de/img/airline/70/XQ.png"
                                alt="SunExpress"
                            />
                            <div className="flight">
                                <h3 className="title">{title}</h3>

                                <div
                                    className="bound"
                                    dangerouslySetInnerHTML={{
                                        __html: connection,
                                    }}></div>
                            </div>
                        </div>
                        <div className="btn">
                            <Link
                                to={`/leistung-stornieren/sitzplatzreservierung/uebersicht/ref=${ref.join()}`}
                                disabled={cardState !== 'active'}
                                className={`button-element ${cardState}`}
                                onClick={() => {
                                    tracking('interaction', 'partialCancellationSeatReservation');
                                }}>
                                {cancellationbuttonText}
                            </Link>
                            {moreCancellationAllowed && (
                                <Link
                                    to={`/leistung-stornieren/sitzplatzreservierung/uebersicht/ref=${ref.join()}`}
                                    className={`button-element`}
                                    onClick={() => {
                                        tracking('interaction', 'partialCancellationSeatReservation');
                                    }}>
                                    {moreCancellationAllowedButtonText}
                                </Link>
                            )}
                        </div>
                    </section>
                );
            }

            if ($this.props.pageState === 'loading') {
                entries.push(
                    <section
                        className="card loading lazy-load-background"
                        key={`${item[0].arrivalAirportCode}-${item[0].flightNumber}`}>
                        <div className="details">
                            <span className="carrier-logo"></span>
                            <div className="flight">
                                <span className="title" style={{ '--width': '100px' }}></span>
                                <span className="bound" style={{ '--width': '400px', '--height': '50px' }}></span>
                            </div>
                        </div>
                        <span className="button" style={{ '--width': '250px', '--height': '16px' }}></span>
                    </section>
                );
            }
        };

        Object.values(legData).forEach((legItem) => {
            Object.keys(legItem).map((item, index) => {
                return logic(legItem[item], index);
            });
        });

        return <>{entries}</>;
    }

    render() {
        const { legData } = this.state;

        return (
            <div className="content-wrapper">
                <h1 className="main-headline">Sitzplatzreservierung stornieren</h1>
                <div className="subline">
                    Bitte wählen Sie den Flug aus, für welchen Sie Sitzplätze stornieren möchten.
                </div>
                <div className="content">
                    <div className="cancellation-info">
                        <i className="material-symbols-outlined">info</i>
                        Sitzplatzreservierungen sind nicht erstattbar. Bitte stornieren Sie diese trotzdem, damit die
                        Plätze wieder freigegeben werden können.
                    </div>
                    {legData && this.Cards()}
                </div>
            </div>
        );
    }
}

export default SeatReservation;
